import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { subHours, getMilliseconds } from 'date-fns';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { Box, Button, Typography, Autocomplete, TextField, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  numToCurrency,
  exportClosing,
  dateDiffInDays,
  netHST,
  numToCurrencyDecimal,
  sortSuites,
  convertAllDates,
  camelToTitle,
  effectivePriceAfterRebate,
  exportIplGrid,
  getOutdoorSizeDisplay,
  getOutdoorType,
} from '../../utils/Functions';
import { selectProject } from '../../features/project/projectSlice';
import VirtualizedTable from '../tables/VirtualizedTable';
import { IUnit, IUnitClosing, IUnitHistory, IPriceList } from '../../types/unit';
import { IDocuments } from '../../types/docusign';
import { IOption, IProjectAdjustment } from '../../types/project';
import { IDeal, IDealAdjustment, IDealDeposit, IDealRealtor, IPurchaserInfo, IThirdParty } from '../../types/CreateDealForm';
import { IUpgrade } from '../../types/colourSelection';
import PageTitle from '../common/PageTitle';
import LoadingLogo from '../common/LoadingLogo';
import { GlobalModal } from '../../features/modal/Modal';
import { FlexBetween, Flex } from '../../commonStyles';
import { useAppDispatch } from '../../app/hooks';
import { handleModal } from '../../features/modal/modalSlice';
import { selectUser } from '../../features/auth/authSlice';
import StandardTable from '../tables/StandardTable';
import '../../css/closingTable.css';

const ClosingTable = () => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const [allColumns, setAllColumns] = useState<any[]>([]);
  const [units, setUnits] = useState<IUnit[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<any[]>([
    {
      Header: 'Suite',
      width: 75,
    },
  ]);
  const [type, setType] = useState<string>('');
  const [pendingDeposits, setPendingDeposits] = useState<string>('');

  const { loading } = useQuery<IUnitClosing>(UNITCLOSING, {
    variables: { project: project._id },
    onCompleted: (data) => {
      const editable = data.unitClosing.map((o) => ({ ...o }));
      let suites = sortSuites(editable, 'unit');
      let unitSuites = sortSuites(suites, 'level');
      setUnits(unitSuites);
    },
  });

  useEffect(() => {
    let newColumns = async () => {
      let depositColumns: any = [];
      let additionalDepositColumns: any = [];
      let options: any = [];
      let miscellaneous: any = [];
      let colourSelection: any = [];
      let amendments: any = [];
      let adjustments: any = [];
      if (units.length > 0) {
        let allDeals = units
          .filter((unit: IUnit) => unit.deal !== null)
          .map((unit: IUnit) => {
            return unit?.deal?.deposit?.length;
          });

        let allAdditional = units
          .filter((unit: IUnit) => unit.deal !== null)
          .map((unit: IUnit) => {
            return unit?.deal?.additionalDeposits?.length;
          });

        let largestAdditionalIndex = await Math.max(...allAdditional);

        let largestIndex = await Math.max(...allDeals);

        let selectedAdditional = units.find((unit: any) => unit.deal && unit.deal.additionalDeposits.length === largestAdditionalIndex);

        let selectedUnit = units.find((unit: any) => unit.deal && unit.deal.deposit.length === largestIndex);

        depositColumns = selectedUnit?.deal?.deposit.map((deposit: IDealDeposit) => {
          return [
            {
              Header: `${deposit.name} Status`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'type', false, pendingDeposits);
                if (result) {
                  return camelToTitle(result.toString());
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `${deposit.name} Percentage`,
              accessor: (rowData: IUnit) => {
                const result = depositAmount(rowData.deal, deposit?.name!, 'percentage', false, pendingDeposits);
                if (result) {
                  return result.toFixed(2) + '%';
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `${deposit.name} Amount`,
              accessor: (rowData: IUnit) => {
                const result = depositAmount(rowData.deal, deposit?.name!, 'amount', false, pendingDeposits);
                if (result) {
                  return numToCurrency.format(result || 0);
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `${deposit.name} Due Date`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'dueDate', false, pendingDeposits);
                if (result) {
                  return subHours(new Date(result || '01/01/2010'), 4)
                    .toISOString()
                    .split('T')[0];
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `${deposit.name} Account Number`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'accountNumber', false, pendingDeposits);
                if (result) {
                  return result;
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `${deposit.name} Cheque Number`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'chequeNumber', false, pendingDeposits);
                if (result) {
                  return result;
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `${deposit.name} Amount Received`,
              accessor: (rowData: IUnit) => {
                const result = depositAmount(rowData.deal, deposit?.name!, 'chequeAmount', false, pendingDeposits);
                if (result) {
                  return numToCurrency.format(result || 0);
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `${deposit.name} Actual Date`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'chequeDate', false, pendingDeposits);
                if (result) {
                  return subHours(new Date(result || '01/01/2010'), 4)
                    .toISOString()
                    .split('T')[0];
                } else {
                  return '-';
                }
              },
            },
          ];
        });

        additionalDepositColumns = selectedAdditional?.deal?.additionalDeposits.map((deposit: IDealDeposit, index: number) => {
          return [
            {
              Header: `Additional Deposit ${index + 1} Status`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'type', true);
                if (result) {
                  return result;
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `Additional Deposit ${index + 1}  Percentage`,
              accessor: (rowData: IUnit) => {
                const result = depositAmount(rowData.deal, deposit?.name!, 'percentage', true);
                if (result) {
                  return result.toFixed(2) + '%';
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `Additional Deposit ${index + 1}  Amount`,
              accessor: (rowData: IUnit) => {
                const result = depositAmount(rowData.deal, deposit?.name!, 'amount', true);
                if (result) {
                  return numToCurrency.format(result || 0);
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `Additional Deposit ${index + 1}  Due Date`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'dueDate', true);
                if (result) {
                  return subHours(new Date(result || '01/01/2010'), 4)
                    .toISOString()
                    .split('T')[0];
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `Additional Deposit ${index + 1}  Account Number`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'accountNumber', true);
                if (result) {
                  return result;
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `Additional Deposit ${index + 1}  Cheque Number`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'chequeNumber', true);
                if (result) {
                  return result;
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `Additional Deposit ${index + 1}  Amount Received`,
              accessor: (rowData: IUnit) => {
                const result = depositAmount(rowData.deal, deposit?.name!, 'chequeAmount', true);
                if (result) {
                  return numToCurrency.format(result || 0);
                } else {
                  return '-';
                }
              },
            },
            {
              Header: `Additional Deposit ${index + 1}  Actual Date`,
              accessor: (rowData: IUnit) => {
                const result = depositType(rowData.deal, deposit?.name!, 'chequeDate', true);
                if (result) {
                  return subHours(new Date(result || '01/01/2010'), 4)
                    .toISOString()
                    .split('T')[0];
                } else {
                  return '-';
                }
              },
            },
          ];
        });

        options = await project.options.map((option: IOption) => {
          return [
            {
              Header: `${option.name}`,
              accessor: (rowData: IUnit) => {
                const result = parkingTotal(rowData.deal, option.name);
                return result;
              },
            },
            {
              Header: `${option.name} Purchased`,
              accessor: (rowData: IUnit) => {
                const result = parkingAmount(rowData.deal, option.name);
                return result;
              },
            },
          ];
        });

        amendments = [
          {
            Header: `Amendments`,
            accessor: (rowData: IUnit) => {
              const result = getAmendments(rowData);
              return result;
            },
          },
          {
            Header: `Broker Coops`,
            accessor: (rowData: IUnit) => {
              const result = getBrokerCoops(rowData);
              return result;
            },
          },
        ];

        miscellaneous = [
          {
            Header: '3rd Party Verification (Name)',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.thirdParty.length > 0) {
                  let result = rowData.deal.thirdParty.map((thirdParty: IThirdParty) => {
                    return `${thirdParty.fullName}`;
                  });
                  return result.join();
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Sales Agent',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.salesRep) {
                  return rowData.deal.salesRep.fullName;
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Waitlist',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.joinWaitlist.length) {
                  let result = rowData.deal.joinWaitlist.map((waitlist: string) => {
                    return `${waitlist}`;
                  });
                  return result.join(', ');
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Ceiling Height',
            accessor: (rowData: IUnit) => {
              if (rowData.ceilingHeight) {
                return rowData.ceilingHeight;
              } else return '-';
            },
          },
          {
            Header: 'Discount (%)',
            accessor: (rowData: IUnit) => '-',
          },
          {
            Header: 'Total Credits',
            accessor: (rowData: IUnit) => {
              let projectCredits = [];
              if (rowData.deal) {
                projectCredits = project.adjustments.filter(
                  (adjustment: IProjectAdjustment) =>
                    adjustment.type === 'Credit' &&
                    !rowData.deal.adjustments.some((dealAdjustment: any) => dealAdjustment.name === adjustment.name)
                );
              } else {
                projectCredits = project.adjustments.filter((adjustment: IProjectAdjustment) => adjustment.type === 'Credit');
              }
              let creditTotal = [];
              for (const adjustment of projectCredits) {
                for (const condition of adjustment.conditions) {
                  const projectAdjustmentValue = (field: string) => {
                    switch (field) {
                      case 'netHstBasePrice':
                        if (condition.condition === 'Less Than') {
                          if (netHST(rowData.basePrice, project) <= condition.value) {
                            return condition.amount;
                          } else return 0;
                        } else if (condition.condition == 'Greater Than') {
                          if (netHST(rowData.basePrice, project) >= condition.value) {
                            return condition.amount;
                          } else return 0;
                        } else return 0;
                      case 'size':
                        if (condition.condition === 'Less Than') {
                          if (rowData.size <= condition.value) {
                            return condition.amount;
                          } else return 0;
                        } else if (condition.condition == 'Greater Than') {
                          if (rowData.size >= condition.value) {
                            return condition.amount;
                          } else return 0;
                        } else return 0;
                      case 'unitTypes':
                        if (condition.condition === 'Includes' && rowData.deal && rowData.deal.deposit.length) {
                          if (adjustment.type === 'ChargePerDeposit') {
                            return condition.amount * rowData.deal.deposit.length;
                          } else if (condition.values.includes(rowData.unitType)) {
                            return condition.amount;
                          } else return 0;
                        } else return 0;
                      case 'modelTypes':
                        if (condition.condition === 'Includes') {
                          if (condition.values.includes(rowData.modelType)) {
                            return condition.amount;
                          } else return 0;
                        } else return 0;
                      case 'maintenance':
                        if (condition.condition === 'Includes') {
                          if (condition.values.includes(rowData.unitType) && rowData.maintenanceFee) {
                            return condition.amount * rowData.maintenanceFee!;
                          } else return 0;
                        } else return 0;
                      default:
                        return;
                    }
                  };
                  let projectUnitValue = projectAdjustmentValue(adjustment.conditionField);
                  creditTotal.push(projectUnitValue);
                }
              }
              let projectCreditTotal = creditTotal.reduce((a: any, b: any) => a + b, 0);
              if (rowData.deal) {
                if (rowData.deal.adjustments.length) {
                  let creditTotal = rowData.deal.adjustments
                    .filter((adjustment: any) => adjustment.type === 'Credit')
                    .reduce((a, b) => a + b.value, 0);
                  return numToCurrencyDecimal.format(creditTotal + projectCreditTotal);
                } else return numToCurrencyDecimal.format(projectCreditTotal);
              } else return numToCurrencyDecimal.format(projectCreditTotal);
            },
          },
          {
            Header: 'Tags',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let result = rowData.deal.tags.map((tag: string) => tag);
                return result.join(', ');
              } else return '';
            },
          },
          {
            Header: 'List Price',
            accessor: (rowData: IUnit) => {
              return numToCurrency.format(rowData.basePrice);
            },
          },
        ];

        colourSelection = [
          {
            Header: 'UPG Session 1',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.upgradeSetOne) {
                  return numToCurrency.format(
                    rowData.deal.upgradeSetOne.upgradeSet
                      .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                      .reduce((a: any, b: any) => {
                        return a + b.price;
                      }, 0)
                  );
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Decor Specialist 1',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.upgradeSetOne) {
                  return rowData.deal.upgradeSetOne.salesRep ? rowData.deal.upgradeSetOne.salesRep.fullName : '-';
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'UPG Session 2',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.upgradeSetTwo) {
                  return numToCurrency.format(
                    rowData.deal.upgradeSetTwo.upgradeSet
                      .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                      .reduce((a: any, b: any) => {
                        return a + b.price;
                      }, 0)
                  );
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Decor Specialist 2',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.upgradeSetTwo) {
                  return rowData.deal.upgradeSetTwo.salesRep ? rowData.deal.upgradeSetTwo.salesRep.fullName : '-';
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'UPG Session 3',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.upgradeSetThree) {
                  return numToCurrency.format(
                    rowData.deal.upgradeSetThree.upgradeSet
                      .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                      .reduce((a: any, b: any) => {
                        return a + b.price;
                      }, 0)
                  );
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Decor Specialist 3',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.upgradeSetThree) {
                  return rowData.deal.upgradeSetThree.salesRep ? rowData.deal.upgradeSetThree.salesRep.fullName : '-';
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Total Decor Cost',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let upgradeOne = 0;
                let upgradeTwo = 0;
                let upgradeThree = 0;
                if (rowData.deal.upgradeSetOne) {
                  upgradeOne = rowData.deal.upgradeSetOne.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                }
                if (rowData.deal.upgradeSetTwo) {
                  upgradeTwo = rowData.deal.upgradeSetTwo.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                }
                if (rowData.deal.upgradeSetThree) {
                  upgradeThree = rowData.deal.upgradeSetThree.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                }

                if (!rowData.deal.upgradeSetOne) return '-';

                return numToCurrencyDecimal.format(parseFloat(((upgradeOne + upgradeTwo + upgradeThree) * 1.13).toFixed(2)));
              } else return '-';
            },
          },
          {
            Header: 'Upgrade Credits',
            accessor: (rowData: IUnit) => {
              if (rowData.deal && (rowData.deal.upgradeSetOne || rowData.deal.upgradeSetTwo || rowData.deal.upgradeSetTwo)) {
                let creditOne = 0;
                let creditTwo = 0;
                let creditThree = 0;
                if (rowData.deal.upgradeSetOne) {
                  let creditUpgradeSetOne = rowData.deal.upgradeSetOne.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetOne) {
                    creditOne = creditUpgradeSetOne.price;
                  }
                }
                if (rowData.deal.upgradeSetTwo) {
                  let creditUpgradeSetTwo = rowData.deal.upgradeSetTwo.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetTwo) {
                    creditTwo = creditUpgradeSetTwo.price;
                  }
                }
                if (rowData.deal.upgradeSetThree) {
                  let creditUpgradeSetThree = rowData.deal.upgradeSetThree.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetThree) {
                    creditThree = creditUpgradeSetThree.price;
                  }
                }

                if (!rowData.deal.upgradeSetOne) return '-';

                return numToCurrency.format(creditOne + creditTwo + creditThree);
              } else return '-';
            },
          },
          {
            Header: 'Total Decor Cost Net Upgrade Credits',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let upgradeOne = 0;
                let upgradeTwo = 0;
                let upgradeThree = 0;
                let creditOne = 0;
                let creditTwo = 0;
                let creditThree = 0;
                if (rowData.deal.upgradeSetOne) {
                  upgradeOne = rowData.deal.upgradeSetOne.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetOne = rowData.deal.upgradeSetOne.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetOne) {
                    creditOne = creditUpgradeSetOne.price;
                  }
                }
                if (rowData.deal.upgradeSetTwo) {
                  upgradeTwo = rowData.deal.upgradeSetTwo.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetTwo = rowData.deal.upgradeSetTwo.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetTwo) {
                    creditTwo = creditUpgradeSetTwo.price;
                  }
                }
                if (rowData.deal.upgradeSetThree) {
                  upgradeThree = rowData.deal.upgradeSetThree.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetThree = rowData.deal.upgradeSetThree.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetThree) {
                    creditThree = creditUpgradeSetThree.price;
                  }
                }

                if (!rowData.deal.upgradeSetOne) return '-';

                return numToCurrencyDecimal.format(
                  parseFloat((upgradeOne + upgradeTwo + upgradeThree - (creditOne + creditTwo + creditThree)).toFixed(2))
                );
              } else return '-';
            },
          },
          {
            Header: 'Total Decor Cost Net HST',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let upgradeOne = 0;
                let upgradeTwo = 0;
                let upgradeThree = 0;
                let creditOne = 0;
                let creditTwo = 0;
                let creditThree = 0;
                if (rowData.deal.upgradeSetOne) {
                  upgradeOne = rowData.deal.upgradeSetOne.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetOne = rowData.deal.upgradeSetOne.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetOne) {
                    creditOne = creditUpgradeSetOne.price;
                  }
                }
                if (rowData.deal.upgradeSetTwo) {
                  upgradeTwo = rowData.deal.upgradeSetTwo.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetTwo = rowData.deal.upgradeSetTwo.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetTwo) {
                    creditTwo = creditUpgradeSetTwo.price;
                  }
                }
                if (rowData.deal.upgradeSetThree) {
                  upgradeThree = rowData.deal.upgradeSetThree.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetThree = rowData.deal.upgradeSetThree.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetThree) {
                    creditThree = creditUpgradeSetThree.price;
                  }
                }

                if (!rowData.deal.upgradeSetOne) return '-';

                return numToCurrencyDecimal.format(
                  parseFloat((upgradeOne + upgradeTwo + upgradeThree - (creditOne + creditTwo + creditThree)).toFixed(2))
                );
              } else return '-';
            },
          },
          {
            Header: 'Total Decor Cost Net HST + Total Purchase Price Net HST',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let upgradeOne = 0;
                let upgradeTwo = 0;
                let upgradeThree = 0;
                let creditOne = 0;
                let creditTwo = 0;
                let creditThree = 0;
                if (rowData.deal.upgradeSetOne) {
                  upgradeOne = rowData.deal.upgradeSetOne.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetOne = rowData.deal.upgradeSetOne.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetOne) {
                    creditOne = creditUpgradeSetOne.price;
                  }
                }
                if (rowData.deal.upgradeSetTwo) {
                  upgradeTwo = rowData.deal.upgradeSetTwo.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetTwo = rowData.deal.upgradeSetTwo.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetTwo) {
                    creditTwo = creditUpgradeSetTwo.price;
                  }
                }
                if (rowData.deal.upgradeSetThree) {
                  upgradeThree = rowData.deal.upgradeSetThree.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetThree = rowData.deal.upgradeSetThree.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetThree) {
                    creditThree = creditUpgradeSetThree.price;
                  }
                }

                return numToCurrencyDecimal.format(
                  parseFloat(
                    (
                      upgradeOne +
                      upgradeTwo +
                      upgradeThree -
                      (creditOne + creditTwo + creditThree) +
                      netHST(rowData.deal.totalPrice!, project)
                    ).toFixed(2)
                  )
                );
              } else return '-';
            },
          },
          {
            Header: 'Total Decor Cost + Total Purchase Price',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let upgradeOne = 0;
                let upgradeTwo = 0;
                let upgradeThree = 0;
                let creditOne = 0;
                let creditTwo = 0;
                let creditThree = 0;
                if (rowData.deal.upgradeSetOne) {
                  upgradeOne = rowData.deal.upgradeSetOne.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetOne = rowData.deal.upgradeSetOne.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetOne) {
                    creditOne = creditUpgradeSetOne.price;
                  }
                }
                if (rowData.deal.upgradeSetTwo) {
                  upgradeTwo = rowData.deal.upgradeSetTwo.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetTwo = rowData.deal.upgradeSetTwo.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetTwo) {
                    creditTwo = creditUpgradeSetTwo.price;
                  }
                }
                if (rowData.deal.upgradeSetThree) {
                  upgradeThree = rowData.deal.upgradeSetThree.upgradeSet
                    .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
                    .reduce((a: any, b: any) => {
                      return a + b.price;
                    }, 0);
                  let creditUpgradeSetThree = rowData.deal.upgradeSetThree.upgradeSet.find(
                    (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
                  );
                  if (creditUpgradeSetThree) {
                    creditThree = creditUpgradeSetThree.price;
                  }
                }

                return numToCurrencyDecimal.format(
                  parseFloat(
                    (
                      (upgradeOne + upgradeTwo + upgradeThree - (creditOne + creditTwo + creditThree)) * 1.13 +
                      rowData.deal?.totalPrice!
                    ).toFixed(2)
                  )
                );
              } else return '-';
            },
          },
        ];

        adjustments = [
          // {
          //   Header: 'Adjustment Total',
          //   accessor: (rowData: IUnit) => {
          //     if (rowData.deal) {
          //       console.log(rowData.deal.adjustments, 'adjustments');
          //       if (rowData.deal.adjustments.length) {
          //         let capsTotal = rowData.deal.adjustments
          //           .filter((adjustment: any) => adjustment.type === 'Charge')
          //           .reduce((a: any, b: any) => {
          //             return a + b.value;
          //           }, 0);
          //         let chargePerDepositTotal = rowData.deal.adjustments
          //           .filter((adjustment: any) => adjustment.type === 'ChargePerDeposit)')
          //           .reduce((a: any, b: any) => {
          //             return a + b.value * rowData.deal.deposit.length;
          //           }, 0);
          //         let creditTotal = rowData.deal.adjustments
          //           .filter((adjustment: any) => adjustment.type === 'Credit')
          //           .reduce((a, b) => a + b.value, 0);
          //         return numToCurrencyDecimal.format(capsTotal + chargePerDepositTotal - creditTotal);
          //       } else return '-';
          //     } else return '-';
          //   },
          // },
          // {
          //   Header: 'Total Price Net HST with Adjustment',
          //   accessor: (rowData: IUnit) => {
          //     if (rowData.deal) {
          //       let upgradeOne = 0;
          //       let upgradeTwo = 0;
          //       let upgradeThree = 0;
          //       let creditOne = 0;
          //       let creditTwo = 0;
          //       let creditThree = 0;
          //       let capsTotal = 0;
          //       let creditTotal = 0;
          //       let chargePerDepositTotal = 0;
          //       if (rowData.deal.upgradeSetOne) {
          //         upgradeOne = rowData.deal.upgradeSetOne.upgradeSet
          //           .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
          //           .reduce((a: any, b: any) => {
          //             return a + b.price;
          //           }, 0);
          //         let creditUpgradeSetOne = rowData.deal.upgradeSetOne.upgradeSet.find(
          //           (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
          //         );
          //         if (creditUpgradeSetOne) {
          //           creditOne = creditUpgradeSetOne.price;
          //         }
          //       }
          //       if (rowData.deal.upgradeSetTwo) {
          //         upgradeTwo = rowData.deal.upgradeSetTwo.upgradeSet
          //           .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
          //           .reduce((a: any, b: any) => {
          //             return a + b.price;
          //           }, 0);
          //         let creditUpgradeSetTwo = rowData.deal.upgradeSetTwo.upgradeSet.find(
          //           (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
          //         );
          //         if (creditUpgradeSetTwo) {
          //           creditTwo = creditUpgradeSetTwo.price;
          //         }
          //       }
          //       if (rowData.deal.upgradeSetThree) {
          //         upgradeThree = rowData.deal.upgradeSetThree.upgradeSet
          //           .filter((upgrade: IUpgrade) => upgrade.mainCategory !== 'Credit')
          //           .reduce((a: any, b: any) => {
          //             return a + b.price;
          //           }, 0);
          //         let creditUpgradeSetThree = rowData.deal.upgradeSetThree.upgradeSet.find(
          //           (upgrade: IUpgrade) => upgrade.mainCategory === 'Credit'
          //         );
          //         if (creditUpgradeSetThree) {
          //           creditThree = creditUpgradeSetThree.price;
          //         }
          //       }
          //       if (rowData.deal.adjustments.length) {
          //         capsTotal = rowData.deal.adjustments
          //           .filter((adjustment: any) => adjustment.type === 'Charge')
          //           .reduce((a: any, b: any) => {
          //             return a + b.value;
          //           }, 0);
          //         chargePerDepositTotal = rowData.deal.adjustments
          //           .filter((adjustment: any) => adjustment.type === 'ChargePerDeposit)')
          //           .reduce((a: any, b: any) => {
          //             return a + (b.value * rowData.deal.deposit.length)
          //           }, 0);
          //         creditTotal = rowData.deal.adjustments
          //           .filter((adjustment: any) => adjustment.type === 'Credit')
          //           .reduce((a, b) => a + b.value, 0);
          //         return numToCurrencyDecimal.format(
          //           parseFloat(
          //             (
          //               (upgradeOne + upgradeTwo + upgradeThree - (creditOne + creditTwo + creditThree)) / 1.13 +
          //               netHST(rowData.deal.totalPrice!) +
          //               capsTotal +
          //               chargePerDepositTotal -
          //               creditTotal
          //             ).toFixed(2)
          //           )
          //         );
          //       } else
          //         return numToCurrencyDecimal.format(
          //           parseFloat(
          //             (
          //               (upgradeOne + upgradeTwo + upgradeThree - (creditOne + creditTwo + creditThree)) / 1.13 +
          //               netHST(rowData.deal.totalPrice!) +
          //               capsTotal -
          //               creditTotal
          //             ).toFixed(2)
          //           )
          //         );
          //     } else return '-';
          //   },
          // },
          {
            Header: 'Rental Guarantee',
            accessor: (rowData: IUnit) => {
              if (rowData) {
                if (rowData.rental) {
                  return numToCurrency.format(rowData.rental);
                }
              } else return '-';
            },
          },
          {
            Header: 'Rental Guarantee Total',
            accessor: (rowData: IUnit) => {
              let projectCredits = [];
              if (rowData.deal) {
                projectCredits = project.adjustments.filter(
                  (adjustment: IProjectAdjustment) =>
                    adjustment.conditionField === 'rental' &&
                    !rowData.deal.adjustments.some((dealAdjustment: any) => dealAdjustment.name === adjustment.name)
                );
              } else {
                projectCredits = project.adjustments.filter((adjustment: IProjectAdjustment) => adjustment.conditionField === 'rental');
              }
              let creditTotal = [];
              for (const adjustment of projectCredits) {
                for (const condition of adjustment.conditions) {
                  const projectAdjustmentValue = (field: string) => {
                    switch (field) {
                      case 'rental':
                        if (condition.condition === 'Includes') {
                          if (condition.values.includes(rowData.unitType) && rowData.rental) {
                            return condition.amount * rowData.rental!;
                          } else return 0;
                        } else return 0;
                      default:
                        return;
                    }
                  };
                  let projectUnitValue = projectAdjustmentValue(adjustment.conditionField);
                  creditTotal.push(projectUnitValue);
                }
              }
              let projectCreditTotal = creditTotal.reduce((a: any, b: any) => a + b, 0);
              if (rowData.deal) {
                if (rowData.deal.adjustments.length) {
                  let creditTotal = rowData.deal.adjustments
                    .filter((adjustment: any) => adjustment.name === 'Rental Guarantee')
                    .reduce((a, b) => a + b.value, 0);
                  return numToCurrencyDecimal.format(creditTotal + projectCreditTotal);
                } else return numToCurrencyDecimal.format(projectCreditTotal);
              } else return numToCurrencyDecimal.format(projectCreditTotal);
            },
          },
          {
            Header: 'Tier',
            accessor: (rowData: IUnit) => {
              if (rowData) {
                if (rowData.tier) {
                  return rowData.tier;
                }
              } else return '-';
            },
          },
          {
            Header: 'Assignment Date',
            accessor: (rowData: IUnit) => {
              if (rowData) {
                if (rowData.assignment && rowData.assignment.signDate) {
                  return convertAllDates(rowData.assignment.signDate, 'PP');
                }
              } else return '-';
            },
          },
          {
            Header: 'Assignors',
            accessor: (rowData: IUnit) => {
              if (rowData) {
                if (rowData.assignment) {
                  return rowData.assignment.assignors.map((purchaser: IPurchaserInfo) => purchaser.fullName).join(', ');
                }
              } else return '-';
            },
          },
          {
            Header: 'RDS Reviewer',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.auditor) {
                  return rowData.deal.auditor.fullName;
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'RDS Review Date',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.auditDate) {
                  return convertAllDates(rowData.deal.auditDate, 'PPpp');
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Solicitor Full Name',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.solicitor?.solicitor) {
                  return rowData.deal.solicitor?.solicitor;
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Solicitor Firm',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.solicitor?.firm) {
                  return rowData.deal.solicitor?.firm;
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Solicitor Address',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let address = '';
                if (rowData.deal.solicitor?.streetAddress) {
                  address += `${rowData.deal.solicitor?.streetAddress}, `;
                }
                if (rowData.deal.solicitor?.city) {
                  address += `${rowData.deal.solicitor?.city}, `;
                }
                if (rowData.deal.solicitor?.province) {
                  address += `${rowData.deal.solicitor?.province}, `;
                }
                if (rowData.deal.solicitor?.postalCode) {
                  address += `${rowData.deal.solicitor?.postalCode}, `;
                }
                return address.trim();
              } else return '-';
            },
          },
          {
            Header: 'Solicitor Email',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.solicitor?.email) {
                  return rowData.deal.solicitor?.email;
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'Solicitor Phone',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.solicitor?.primaryPhone) {
                  return rowData.deal.solicitor?.primaryPhone;
                } else return '-';
              } else return '-';
            },
          },
          {
            Header: 'First Tentative Occupancy',
            accessor: (rowData: IUnit) => {
              if (rowData.firstTentativeOccupancy) {
                return convertAllDates(new Date(rowData.firstTentativeOccupancy), 'PP');
              } else return '-';
            },
          },
          {
            Header: 'Final Tentative Occupancy',
            accessor: (rowData: IUnit) => {
              if (rowData.finalTentativeOccupancy) {
                return convertAllDates(new Date(rowData.finalTentativeOccupancy), 'PP');
              } else return '-';
            },
          },
          {
            Header: 'Outside Occupancy',
            accessor: (rowData: IUnit) => {
              if (rowData.outsideOccupancy) {
                return convertAllDates(new Date(rowData.outsideOccupancy), 'PP');
              } else return '-';
            },
          },
          {
            Header: 'Firm Occupancy',
            accessor: (rowData: IUnit) => {
              if (rowData.firmOccupancy) {
                return convertAllDates(new Date(rowData.firmOccupancy), 'PP');
              } else return '-';
            },
          },
          {
            Header: 'Outstanding Status',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                if (rowData.deal.type) {
                  return `${camelToTitle(rowData.deal.type)}`;
                } else return '-';
              }
            },
          },
          {
            Header: 'Effective Price (HST Rebate)',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                return numToCurrencyDecimal.format(effectivePriceAfterRebate(rowData.deal.basePrice, project));
              } else {
                return numToCurrencyDecimal.format(effectivePriceAfterRebate(rowData.basePrice, project));
              }
            },
          },
          // Forfeits from the unit's rescinded deals (worked out in the unitClosing query): deposits and additional
          // deposits whose cheque type is 'forfeited'
          {
            Header: 'MRT Additional Deposit',
            accessor: (rowData: any) => numToCurrencyDecimal.format(rowData.mrtAdditionalDeposit || 0),
          },
          {
            Header: 'Forfeited Deposit Revenue',
            accessor: (rowData: any) => numToCurrencyDecimal.format(rowData.forfeitedDepositRevenue || 0),
          },
          {
            Header: 'Total Forfeited Deposit Revenue',
            accessor: (rowData: any) => numToCurrencyDecimal.format((rowData.mrtAdditionalDeposit || 0) + (rowData.forfeitedDepositRevenue || 0)),
          },
          {
            Header: 'Unit Revenue + Forfeit Revenue',
            accessor: (rowData: any) =>
              numToCurrencyDecimal.format(
                netHST(rowData.basePrice, project) + (rowData.mrtAdditionalDeposit || 0) + (rowData.forfeitedDepositRevenue || 0)
              ),
          },
          {
            Header: 'Identifier',
            accessor: (rowData: IUnit) => {
              if (rowData.deal) {
                let uniqueDate = getMilliseconds(new Date(rowData.deal.createdAt)).toString();
                let uniqueId = rowData.deal._id!.slice(-5);
                let unitTransfer = rowData.history.find(
                  (history: IUnitHistory) => history && history.type === 'Unit Transfer' && history.description.includes('transferred to')
                );
                if (unitTransfer) {
                  let previousSuite = unitTransfer.description.match('Transfer(.*)transferred');
                  if (previousSuite) {
                    return uniqueDate.concat(uniqueId).concat(`-${previousSuite[1].trim()}`);
                  }
                } else {
                  return uniqueDate.concat(uniqueId);
                }
              } else return '-';
            },
          },
        ];
      }

      const subItems = depositColumns ? depositColumns.flat() : [];
      const subAdditionalItems = additionalDepositColumns ? additionalDepositColumns.flat() : [];
      let subOptions = options.flat();
      const totalColumns = [
        ...columns,
        ...subOptions,
        ...secondColumn,
        ...subItems,
        ...subAdditionalItems,
        ...amendments,
        ...miscellaneous,
        ...colourSelection,
        ...adjustments,
      ];
      setAllColumns(totalColumns);
      if (pendingDeposits === 'pending' || pendingDeposits === 'original') {
        exportClosing(`${project.name} ${convertAllDates(new Date(), 'P')} Closing.csv`, totalColumns, units);
      }
    };
    newColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, pendingDeposits]);

  const getColumnWidth = (rows: any, accessor: string, headerText: string) => {
    const maxWidth = 600;
    const magicSpacing = 20;
    const cellLength = Math.max(...rows.map((row: any) => (`${row[accessor]}` || '').length), headerText.length);

    return Math.min(maxWidth, cellLength * magicSpacing);
  };

  const depositType = (deal: IDeal, depositName: string, type: string, additional: boolean = false, deposits: string = '') => {
    if (deal) {
      let deposit;
      if (additional) {
        deposit = deal.additionalDeposits.find((d) => d.name === depositName);
      } else {
        if (deposits === 'pending' && deal.pendingDeposits.length) {
          deposit = deal.pendingDeposits.find((d) => d.name === depositName);
        } else {
          deposit = deal.deposit.find((d) => d.name === depositName);
        }
      }
      if (deposit) {
        if (type === 'type') {
          return deposit ? deposit.chequeType : null;
        } else if (type === 'dueDate') {
          return deposit?.dueDate ? deposit.dueDate : null;
        } else if (type === 'chequeDate') {
          return deposit?.chequeDate ? deposit.chequeDate : null;
        } else if (type === 'accountNumber') {
          return deposit?.accountNumber ? deposit.accountNumber : null;
        } else if (type === 'chequeNumber') {
          return deposit?.chequeNumber ? deposit.chequeNumber : null;
        } else if (type === 'amountReceived') {
          return deposit?.chequeAmount ? deposit.chequeAmount : null;
        }
      } else return null;
    } else return null;
  };

  const depositAmount = (deal: IDeal, depositType: string, type: string, additional: boolean = false, deposits: string = '') => {
    if (deal) {
      let deposit;
      if (additional) {
        deposit = deal.additionalDeposits.find((d) => d.name === depositType);
      } else {
        if (deposits === 'pending' && deal.pendingDeposits.length) {
          deposit = deal.pendingDeposits.find((d) => d.name === depositType);
        } else {
          deposit = deal.deposit.find((d) => d.name === depositType);
        }
      }
      if (type === 'amount') {
        return deposit ? deposit.amount : null;
      } else if (type === 'chequeAmount') {
        return deposit ? deposit.chequeAmount : null;
      } else if (type === 'percentage') {
        return deposit ? (deposit?.amount! / deal?.totalPrice!) * 100 : null;
      }
    } else return null;
  };

  const parkingTotal = (deal: IDeal, type: string) => {
    if (deal) {
      const option = deal.options.find((d) => d.name === type);
      return option ? numToCurrency.format(option?.amount!) : '-';
    } else return '-';
  };

  const parkingAmount = (deal: IDeal, type: string) => {
    if (deal) {
      const option = deal.options.find((d) => d.name === type);
      return option ? option.purchaseAmount : '-';
    } else return '-';
  };

  const getAmendments = (unit: IUnit) => {
    if (unit.deal) {
      let result = unit.deal.documents.map((document: IDocuments) => {
        if (document.type !== 'Coop') {
          return `${document.name} - ${document.status}`;
        }
      });
      return result.join(', ');
    } else return '-';
  };

  const getBrokerCoops = (unit: IUnit) => {
    if (unit.deal) {
      let result = unit.deal.documents.find((document: IDocuments) => {
        return document.type === 'Coop';
      });
      if (result) {
        return result.status;
      } else return '-';
    } else return '-';
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Suite',
        accessor: (rowData: IUnit) => rowData.suite,
        width: 75,
      },
      {
        Header: 'Count',
        accessor: (rowData: IUnit, index: number) => index + 1,
        width: 75,
      },
      {
        Header: 'Status',
        accessor: (rowData: IUnit) => rowData.status,
        width: 75,
      },
      {
        Header: 'Allocation',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              let realtors = rowData.deal.realtor
                .map((realtor: IDealRealtor) => {
                  return `${realtor.fullName}`;
                })
                .join(', ');
              return realtors;
            } else return '';
          } else {
            if (rowData.allocation) {
              return rowData.allocation.fullName;
            } else if (rowData.tempAllocation) {
              return rowData.tempAllocation;
            } else return '';
          }
        },
      },
      {
        Header: 'Allocation Type',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal?.tags.includes('Friends and Family')) {
              return 'Friends and Family';
            } else if (!rowData.deal.realtor.length) {
              return 'Public';
            } else if (rowData.deal.realtor.length) {
              return 'Agent';
            } else return '';
          } else return '';
        },
      },
      {
        Header: 'Unit',
        accessor: (rowData: IUnit) => rowData.unit,
      },
      {
        Header: 'Level',
        field: 'level',
        accessor: (rowData: IUnit) => rowData.level,
      },
      {
        Header: 'Model',
        field: 'model',
        accessor: (rowData: IUnit) => rowData.modelType,
      },
      {
        Header: 'Outdoor Type',
        field: 'outdoorType',
        accessor: (rowData: IUnit) => getOutdoorType(rowData.outdoor),
      },
      {
        Header: 'Size',
        field: 'size',
        accessor: (rowData: IUnit) => rowData.size,
      },
      {
        Header: 'Unit Type',
        field: 'unitType',
        accessor: (rowData: IUnit) => rowData.unitType,
      },
      {
        Header: 'Exposure',
        field: 'exposure',
        accessor: (rowData: IUnit) => rowData.exposure,
      },
      {
        Header: 'Bathroom',
        field: 'bathroom',
        accessor: (rowData: IUnit) => rowData.bathroom,
      },
      {
        Header: 'Outdoor Size',
        field: 'outdoorSize',
        accessor: (rowData: IUnit) => getOutdoorSizeDisplay(rowData.outdoor),
      },
      {
        Header: 'PPSF',
        field: 'PPSF',
        accessor: (rowData: IUnit) => numToCurrency.format(rowData.basePrice / rowData.size),
      },
      {
        Header: 'Unit Price',
        field: 'unitPrice',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            return numToCurrency.format(rowData.deal.basePrice);
          } else {
            return numToCurrency.format(rowData.basePrice);
          }
        },
      },
      {
        Header: 'Unit Price Net of HST',
        field: 'unitPrice',
        accessor: (rowData: IUnit) => {
          let adjustment = 0;
          if (rowData.deal && rowData.deal.adjustments.length) {
            let creditAdjustment = rowData.deal.adjustments.find(
              (adjustment: IDealAdjustment) => adjustment.adjustmentType === 'CreditDeductedFromNetUnitPrice'
            );
            if (creditAdjustment) {
              adjustment = creditAdjustment.value;
            }
          }
          return rowData.type === 'commercial'
            ? numToCurrencyDecimal.format(rowData.basePrice)
            : numToCurrencyDecimal.format(netHST(rowData.basePrice, project) - adjustment);
        },
      },
      {
        Header: 'Total Price',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            return numToCurrency.format(rowData.deal?.totalPrice!);
          } else return '-';
        },
      },
      {
        Header: 'Total Price Net of HST',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            let adjustment = 0;
            if (rowData.deal && rowData.deal.adjustments.length) {
              let creditAdjustment = rowData.deal.adjustments.find(
                (adjustment: IDealAdjustment) => adjustment.adjustmentType === 'CreditDeductedFromNetUnitPrice'
              );
              if (creditAdjustment) {
                adjustment = creditAdjustment.value;
              }
            }

            if (rowData.type === 'commercial') return numToCurrencyDecimal.format(rowData.deal?.totalPrice!);

            // On netHst projects only the deal price is net of HST; options are added on top as-is.
            if (project.netHst) {
              const totalOptions = rowData.deal.options.reduce((a, b) => {
                return a + b?.amount!;
              }, 0);
              console.log(rowData.deal.basePrice, 'basePrice');
              return numToCurrencyDecimal.format(netHST(rowData.deal.basePrice, project) + totalOptions - adjustment);
            }

            return numToCurrencyDecimal.format(netHST(rowData.deal?.totalPrice!, project) - adjustment);
          } else return '-';
        },
      },
      {
        Header: 'Deal Sent Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            let result = convertAllDates(rowData.deal.createdAt, 'PP');
            return result;
          } else return '-';
        },
      },
      {
        Header: 'Signing Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.signDate) {
              return convertAllDates(rowData.deal.signDate, 'PP');
            } else return '-';
          } else return '-';
        },
      },
      {
        Header: 'Execute Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.executeDate) {
              return convertAllDates(new Date(rowData.deal.executeDate), 'PP');
            } else return '-';
          } else return '-';
        },
      },
      {
        Header: 'Firming Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.firmDate) {
              return convertAllDates(new Date(rowData.deal.firmDate), 'PP');
            } else return '-';
          } else return '-';
        },
      },
      {
        Header: 'Extension',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal?.signDate || rowData.deal?.firmDate) {
              const signDate = new Date(rowData.deal?.signDate!);
              const firmDate = new Date(rowData.deal?.firmDate!);
              const dateDifference = dateDiffInDays(signDate, firmDate);
              return dateDifference > 10 ? 'Yes' : 'No';
            } else return '-';
          }
          return '-';
        },
      },
    ],
    []
  );

  const secondColumn = useMemo(() => {
    return [
      {
        Header: 'Purchaser One First Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].firstName;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].firstName;
          } else return '-';
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One First Name'),
      },
      {
        Header: 'Purchaser One Last Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].lastName;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].lastName;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One DOB',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].dob
              ? subHours(new Date(rowData.assignment.assignees[0].dob), 5).toISOString().split('T')[0]
              : '';
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].dob ? subHours(new Date(rowData.deal.purchasers[0].dob), 5).toISOString().split('T')[0] : '';
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Street Address',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            let address = `${rowData.assignment.assignees[0].unit ? rowData.assignment.assignees[0].unit : ''} ${
              rowData.assignment.assignees[0].streetAddress
            }`;
            return address.trim();
          } else if (rowData.deal) {
            let address = `${rowData.deal.purchasers[0].unit ? rowData.deal.purchasers[0].unit : ''} ${
              rowData.deal.purchasers[0].streetAddress
            }`;
            return address.trim();
          } else return '-';
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One Street Address'),
      },
      {
        Header: 'Purchaser One City',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].city;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].city;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Province',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].province;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].province;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Postal Code',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].postalCode;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].postalCode;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].primaryPhone;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].primaryPhone;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One E-mail',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].email;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].email;
          } else return '-';
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One E-mail'),
      },
      {
        Header: 'Purchaser One SIN',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            if (user.type === 'Manager' || user.type === 'Developer') {
              return rowData.assignment.assignees[0].sin;
            } else return 'received';
          } else if (rowData.deal && (user.type === 'Manager' || user.type === 'Developer')) {
            return rowData.deal.purchasers[0].sin;
          } else if (rowData.deal && rowData.deal.purchasers[0] && rowData.deal.purchasers[0].sin) {
            return 'received';
          } else return 'N/A';
        },
      },
      {
        Header: 'Purchaser One Employer',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].employer;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].employer;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Occupation',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].occupation;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].occupation;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Purchaser Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].purchaserType;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].purchaserType;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One ID Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].idType;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].idType;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One ID Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].idNumber;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].idNumber;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One ID Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].idExpiry
              ? subHours(new Date(rowData.assignment.assignees[0].idExpiry), 5).toISOString().split('T')[0]
              : '';
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].idExpiry
              ? subHours(new Date(rowData.deal.purchasers[0].idExpiry), 5).toISOString().split('T')[0]
              : '';
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One ID Jurisdiction',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].idJurisdiction;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].idJurisdiction;
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Proof Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].proofType ? rowData.assignment.assignees[0].proofType : '-';
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].proofType ? rowData.deal.purchasers[0].proofType : '-';
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Proof Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].proofNumber ? rowData.assignment.assignees[0].proofNumber : '-';
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].proofNumber ? rowData.deal.purchasers[0].proofNumber : '-';
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Proof Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].proofExpiry
              ? subHours(new Date(rowData.assignment.assignees[0].proofExpiry), 5).toISOString().split('T')[0]
              : '-';
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].proofExpiry
              ? subHours(new Date(rowData.deal.purchasers[0].proofExpiry), 5).toISOString().split('T')[0]
              : '-';
          } else return '-';
        },
      },
      {
        Header: 'Purchaser One Business Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment && rowData.assignment.assignees.length) {
            return rowData.assignment.assignees[0].businessNumber;
          } else if (rowData.deal) {
            return rowData.deal.purchasers[0].businessNumber ? rowData.deal.purchasers[0].businessNumber : '-';
          } else return '-';
        },
      },
      {
        Header: 'Purchaser Two First Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].firstName;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].firstName;
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser Two First Name'),
      },
      {
        Header: 'Purchaser Two Last Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].lastName;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].lastName;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two D.O.B',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].dob
                ? subHours(new Date(rowData.assignment.assignees[1].dob), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].dob
                ? subHours(new Date(rowData.deal.purchasers[1].dob), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Street Address',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              let address = `${rowData.assignment.assignees[1].unit ? rowData.assignment.assignees[1].unit : ''} ${
                rowData.assignment.assignees[1].streetAddress ? rowData.assignment.assignees[1].streetAddress : ''
              }`;
              return address.trim();
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              let address = `${rowData.deal.purchasers[1].unit ? rowData.deal.purchasers[1].unit : ''} ${
                rowData.deal.purchasers[1].streetAddress ? rowData.deal.purchasers[1].streetAddress : ''
              }`;
              return address.trim();
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One Street Address'),
      },
      {
        Header: 'Purchaser Two City',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].city;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].city;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Province',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].province;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].province;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Postal Code',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].postalCode;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].postalCode;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].primaryPhone;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].primaryPhone;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two E-mail',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].email;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].email;
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One E-mail'),
      },
      {
        Header: 'Purchaser Two SIN',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              if (user.type === 'Manager' || user.type === 'Developer') {
                return rowData.assignment.assignees[1].sin;
              } else return 'received';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              if (user.type === 'Manager' || user.type === 'Developer') {
                return rowData.deal.purchasers[1].sin;
              } else return 'received';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Employer',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].employer;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].employer;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Occupation',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].occupation;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].occupation;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Purchaser Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].purchaserType;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].purchaserType;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two ID Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].idType;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].idType;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two ID Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].idNumber;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].idNumber;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two ID Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].idExpiry
                ? subHours(new Date(rowData.assignment.assignees[1].idExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].idExpiry
                ? subHours(new Date(rowData.deal.purchasers[1].idExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two ID Jurisdiction',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].idJurisdiction;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].idJurisdiction;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Proof Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].proofType ? rowData.assignment.assignees[1].proofType : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].proofType ? rowData.deal.purchasers[1].proofType : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Proof Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].proofNumber ? rowData.assignment.assignees[1].proofNumber : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].proofNumber ? rowData.deal.purchasers[1].proofNumber : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Proof Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].proofExpiry
                ? subHours(new Date(rowData.assignment.assignees[1].proofExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].proofExpiry
                ? subHours(new Date(rowData.deal.purchasers[1].proofExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Two Business Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 1) {
              return rowData.assignment.assignees[1].businessNumber ? rowData.assignment.assignees[1].businessNumber : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 1) {
              return rowData.deal.purchasers[1].businessNumber ? rowData.deal.purchasers[1].businessNumber : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three First Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].firstName;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].firstName;
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One First Name'),
      },
      {
        Header: 'Purchaser Three Last Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].lastName;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].lastName;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three D.O.B',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].dob
                ? subHours(new Date(rowData.assignment.assignees[2].dob), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].dob
                ? subHours(new Date(rowData.deal.purchasers[2].dob), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Street Address',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              let address = `${rowData.assignment.assignees[2].unit ? rowData.assignment.assignees[2].unit : ''} ${
                rowData.assignment.assignees[2].streetAddress ? rowData.assignment.assignees[2].streetAddress : ''
              }`;
              return address.trim();
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              let address = `${rowData.deal.purchasers[2].unit ? rowData.deal.purchasers[2].unit : ''} ${
                rowData.deal.purchasers[2].streetAddress ? rowData.deal.purchasers[2].streetAddress : ''
              }`;
              return address.trim();
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One Street Address'),
      },
      {
        Header: 'Purchaser Three City',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].city;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].city;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Province',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].province;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].province;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Postal Code',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].postalCode;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].postalCode;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].primaryPhone;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].primaryPhone;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three E-mail',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].email;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].email;
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One E-mail'),
      },
      {
        Header: 'Purchaser Three SIN',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              if (user.type === 'Manager' || user.type === 'Developer') {
                return rowData.assignment.assignees[2].sin;
              } else return 'received';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              if (user.type === 'Manager' || user.type === 'Developer') {
                return rowData.deal.purchasers[2].sin;
              } else return 'received';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Employer',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].employer;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].employer;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Occupation',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].occupation;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].occupation;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Purchaser Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].purchaserType;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].purchaserType;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three ID Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].idType;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].idType;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three ID Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].idNumber;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].idNumber;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three ID Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].idExpiry
                ? subHours(new Date(rowData.assignment.assignees[2].idExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].idExpiry
                ? subHours(new Date(rowData.deal.purchasers[2].idExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three ID Jurisdiction',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].idJurisdiction;
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].idJurisdiction;
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Proof Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].proofType ? rowData.assignment.assignees[2].proofType : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].proofType ? rowData.deal.purchasers[2].proofType : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Proof Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].proofNumber ? rowData.assignment.assignees[2].proofNumber : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].proofNumber ? rowData.deal.purchasers[2].proofNumber : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Proof Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].proofExpiry
                ? subHours(new Date(rowData.assignment.assignees[2].proofExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].proofExpiry
                ? subHours(new Date(rowData.deal.purchasers[2].proofExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Three Business Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 2) {
              return rowData.assignment.assignees[2].businessNumber ? rowData.assignment.assignees[2].businessNumber : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 2) {
              return rowData.deal.purchasers[2].businessNumber ? rowData.deal.purchasers[2].businessNumber : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four First Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].firstName ? rowData.assignment.assignees[3].firstName : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].firstName ? rowData.deal.purchasers[3].firstName : '-';
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One First Name'),
      },
      {
        Header: 'Purchaser Four Last Name',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].lastName ? rowData.assignment.assignees[3].lastName : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].lastName ? rowData.deal.purchasers[3].lastName : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four D.O.B',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].dob
                ? subHours(new Date(rowData.assignment.assignees[3].dob), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].dob
                ? subHours(new Date(rowData.deal.purchasers[3].dob), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Street Address',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              let address = `${rowData.assignment.assignees[3].unit ? rowData.assignment.assignees[3].unit : ''} ${
                rowData.assignment.assignees[3].streetAddress ? rowData.assignment.assignees[3].streetAddress : ''
              }`;
              return address.trim();
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              let address = `${rowData.deal.purchasers[3].unit ? rowData.deal.purchasers[3].unit : ''} ${
                rowData.deal.purchasers[3].streetAddress ? rowData.deal.purchasers[3].streetAddress : ''
              }`;
              return address.trim();
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One Street Address'),
      },
      {
        Header: 'Purchaser Four City',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].city ? rowData.assignment.assignees[3].city : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].city ? rowData.deal.purchasers[3].city : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Province',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].province ? rowData.assignment.assignees[3].province : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].province ? rowData.deal.purchasers[3].province : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Postal Code',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].postalCode ? rowData.assignment.assignees[3].postalCode : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].postalCode ? rowData.deal.purchasers[3].postalCode : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].primaryPhone ? rowData.assignment.assignees[3].primaryPhone : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].primaryPhone ? rowData.deal.purchasers[3].primaryPhone : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four E-mail',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].email ? rowData.assignment.assignees[3].email : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].email ? rowData.deal.purchasers[3].email : '-';
            } else return '-';
          }
        },
        width: getColumnWidth(allColumns, 'accessor', 'Purchaser One E-mail'),
      },
      {
        Header: 'Purchaser Four SIN',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              if (user.type === 'Manager' || user.type === 'Developer') {
                return rowData.assignment.assignees[3].sin;
              } else return 'received';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              if (user.type === 'Manager' || user.type === 'Developer') {
                return rowData.deal.purchasers[3].sin;
              } else return 'received';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Employer',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].employer ? rowData.assignment.assignees[3].employer : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].employer ? rowData.deal.purchasers[3].employer : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Occupation',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].occupation ? rowData.assignment.assignees[3].occupation : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].occupation ? rowData.deal.purchasers[3].occupation : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Purchaser Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].purchaserType ? rowData.assignment.assignees[3].purchaserType : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].purchaserType ? rowData.deal.purchasers[3].purchaserType : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four ID Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].idType ? rowData.assignment.assignees[3].idType : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].idType ? rowData.deal.purchasers[3].idType : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four ID Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].idNumber ? rowData.assignment.assignees[3].idNumber : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].idNumber ? rowData.deal.purchasers[3].idNumber : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four ID Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].idExpiry
                ? subHours(new Date(rowData.assignment.assignees[3].idExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].idExpiry
                ? subHours(new Date(rowData.deal.purchasers[3].idExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four ID Jurisdiction',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].idJurisdiction ? rowData.assignment.assignees[3].idJurisdiction : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].idJurisdiction ? rowData.deal.purchasers[3].idJurisdiction : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Proof Type',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].proofType ? rowData.assignment.assignees[3].proofType : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].proofType ? rowData.deal.purchasers[3].proofType : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Proof Number',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].proofNumber ? rowData.assignment.assignees[3].proofNumber : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].proofNumber ? rowData.deal.purchasers[3].proofNumber : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Purchaser Four Proof Expiry',
        accessor: (rowData: IUnit) => {
          if (rowData.assignment) {
            if (rowData.assignment && rowData.assignment.assignees && rowData.assignment.assignees.length > 3) {
              return rowData.assignment.assignees[3].proofExpiry
                ? subHours(new Date(rowData.assignment.assignees[3].proofExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          } else {
            if (rowData.deal && rowData.deal.purchasers.length > 3) {
              return rowData.deal.purchasers[3].proofExpiry
                ? subHours(new Date(rowData.deal.purchasers[3].proofExpiry), 5).toISOString().split('T')[0]
                : '-';
            } else return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Full Name',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].fullName;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Email',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].email;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Brokerage',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].brokerage;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Street Address',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].streetAddress;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 City',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].city;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Province',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].province;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Country',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].country;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Postal Code',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].postalCode;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Brokerage Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].brokeragePhone;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Direct Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].directPhone;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Brokerage Fax',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].brokerageFax;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 First Commission Type',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].coopRates.length ? rowData.deal.realtor[0].coopRates[0].type : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 First Commission Percent',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].coopRates.length && rowData.deal.realtor[0].coopRates[0].percentage
                ? `${rowData.deal.realtor[0].coopRates[0].percentage}%`
                : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 First Commission Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              if (rowData.deal.realtor[0].coopRates.length && rowData.deal.realtor[0].coopRates[0].date) {
                return convertAllDates(rowData.deal.realtor[0].coopRates[0].date, 'PP');
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 First Commission Amount',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              if (rowData.deal.realtor[0].coopRates.length && rowData.deal.realtor[0].coopRates[0].fixedAmount) {
                return numToCurrencyDecimal.format(parseFloat(rowData.deal.realtor[0].coopRates[0].fixedAmount));
              } else if (rowData.deal.realtor[0].coopRates.length && rowData.deal.realtor[0].coopRates[0].percentage) {
                if (project.commissionIncludeOptions) {
                  return numToCurrencyDecimal.format(
                    netHST(
                      rowData.basePrice +
                        rowData.deal.options.reduce((a, b) => {
                          return a + b?.amount!;
                        }, 0),
                      project,
                    ) *
                      (parseFloat(rowData.deal.realtor[0].coopRates[0].percentage) / 100)
                  );
                } else {
                  return numToCurrencyDecimal.format(
                    netHST(rowData.basePrice, project) * (parseFloat(rowData.deal.realtor[0].coopRates[0].percentage) / 100)
                  );
                }
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Second Commission Type ',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].coopRates.length > 1 ? rowData.deal.realtor[0].coopRates[1].type : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Second Commission Percent',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].coopRates.length > 1 && rowData.deal.realtor[0].coopRates[1].percentage
                ? `${rowData.deal.realtor[0].coopRates[1].percentage}%`
                : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Second Commission Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              if (rowData.deal.realtor[0].coopRates.length > 1 && rowData.deal.realtor[0].coopRates[1].date) {
                return convertAllDates(rowData.deal.realtor[0].coopRates[1].date, 'PP');
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Second Commission Amount',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              if (rowData.deal.realtor[0].coopRates.length > 1 && rowData.deal.realtor[0].coopRates[1].fixedAmount) {
                return numToCurrencyDecimal.format(parseFloat(rowData.deal.realtor[0].coopRates[1].fixedAmount));
              } else if (rowData.deal.realtor[0].coopRates.length > 1 && rowData.deal.realtor[0].coopRates[1].percentage) {
                if (project.commissionIncludeOptions) {
                  return numToCurrencyDecimal.format(
                    netHST(
                      rowData.basePrice +
                        rowData.deal.options.reduce((a, b) => {
                          return a + b?.amount!;
                        }, 0),
                      project,
                    ) *
                      (parseFloat(rowData.deal.realtor[0].coopRates[1].percentage) / 100)
                  );
                } else {
                  return numToCurrencyDecimal.format(
                    netHST(rowData.basePrice, project) * (parseFloat(rowData.deal.realtor[0].coopRates[1].percentage) / 100)
                  );
                }
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Third Commission Type ',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].coopRates.length > 2 ? rowData.deal.realtor[0].coopRates[2].type : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Third Commission Percent',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              return rowData.deal.realtor[0].coopRates.length > 2 && rowData.deal.realtor[0].coopRates[2].percentage
                ? `${rowData.deal.realtor[0].coopRates[2].percentage}%`
                : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Third Commission Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              if (rowData.deal.realtor[0].coopRates.length > 2 && rowData.deal.realtor[0].coopRates[2].date) {
                return convertAllDates(rowData.deal.realtor[0].coopRates[2].date, 'PP');
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 1 Third Commission Amount',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              if (rowData.deal.realtor[0].coopRates.length > 2 && rowData.deal.realtor[0].coopRates[2].fixedAmount) {
                return numToCurrencyDecimal.format(parseFloat(rowData.deal.realtor[0].coopRates[2].fixedAmount));
              } else if (rowData.deal.realtor[0].coopRates.length > 2 && rowData.deal.realtor[0].coopRates[2].percentage) {
                if (project.commissionIncludeOptions) {
                  return numToCurrencyDecimal.format(
                    netHST(
                      rowData.basePrice +
                        rowData.deal.options.reduce((a, b) => {
                          return a + b?.amount!;
                        }, 0),
                      project,
                    ) *
                      (parseFloat(rowData.deal.realtor[0].coopRates[2].percentage) / 100)
                  );
                } else {
                  return numToCurrencyDecimal.format(
                    netHST(rowData.basePrice, project) * (parseFloat(rowData.deal.realtor[0].coopRates[2].percentage) / 100)
                  );
                }
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Full Name',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].fullName;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Email',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].email;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Brokerage',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].brokerage;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Street Address',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].streetAddress;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 City',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].city;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Province',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].province;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Country',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].country;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Postal Code',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].postalCode;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Brokerage Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].brokeragePhone;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Direct Phone',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].directPhone;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Brokerage Fax',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].brokerageFax;
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 First Commission Type',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].coopRates.length ? rowData.deal.realtor[1].coopRates[0].type : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 First Commission Percent',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].coopRates.length && rowData.deal.realtor[1].coopRates[0].percentage
                ? `${rowData.deal.realtor[1].coopRates[0].percentage}%`
                : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 First Commission Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              if (rowData.deal.realtor[1].coopRates.length && rowData.deal.realtor[1].coopRates[0].date) {
                return convertAllDates(rowData.deal.realtor[1].coopRates[0].date, 'PP');
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 First Commission Amount',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              if (rowData.deal.realtor[1].coopRates.length && rowData.deal.realtor[1].coopRates[0].fixedAmount) {
                return numToCurrencyDecimal.format(parseFloat(rowData.deal.realtor[1].coopRates[0].fixedAmount));
              } else if (rowData.deal.realtor[1].coopRates.length && rowData.deal.realtor[1].coopRates[0].percentage) {
                if (project.commissionIncludeOptions) {
                  return numToCurrencyDecimal.format(
                    netHST(
                      rowData.basePrice +
                        rowData.deal.options.reduce((a, b) => {
                          return a + b?.amount!;
                        }, 0),
                      project,
                    ) *
                      (parseFloat(rowData.deal.realtor[1].coopRates[0].percentage) / 100)
                  );
                } else {
                  return numToCurrencyDecimal.format(
                    netHST(rowData.basePrice, project) * (parseFloat(rowData.deal.realtor[1].coopRates[0].percentage) / 100)
                  );
                }
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Second Commission Type ',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].coopRates.length > 1 ? rowData.deal.realtor[1].coopRates[1].type : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Second Commission Percent',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].coopRates.length > 1 && rowData.deal.realtor[1].coopRates[1].percentage
                ? `${rowData.deal.realtor[1].coopRates[1].percentage}%`
                : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Second Commission Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              if (rowData.deal.realtor[1].coopRates.length > 1 && rowData.deal.realtor[1].coopRates[1].date) {
                return convertAllDates(rowData.deal.realtor[1].coopRates[1].date, 'PP');
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Second Commission Amount',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              if (rowData.deal.realtor[1].coopRates.length > 1 && rowData.deal.realtor[1].coopRates[1].fixedAmount) {
                return numToCurrencyDecimal.format(parseFloat(rowData.deal.realtor[1].coopRates[1].fixedAmount));
              } else if (rowData.deal.realtor[1].coopRates.length > 1 && rowData.deal.realtor[1].coopRates[1].percentage) {
                if (project.commissionIncludeOptions) {
                  return numToCurrencyDecimal.format(
                    netHST(
                      rowData.basePrice +
                        rowData.deal.options.reduce((a, b) => {
                          return a + b?.amount!;
                        }, 0),
                      project,
                    ) *
                      (parseFloat(rowData.deal.realtor[1].coopRates[1].percentage) / 100)
                  );
                } else {
                  return numToCurrencyDecimal.format(
                    netHST(rowData.basePrice, project) * (parseFloat(rowData.deal.realtor[1].coopRates[1].percentage) / 100)
                  );
                }
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Third Commission Type ',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].coopRates.length > 2 ? rowData.deal.realtor[1].coopRates[2].type : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Third Commission Percent',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              return rowData.deal.realtor[1].coopRates.length > 2 && rowData.deal.realtor[1].coopRates[2].percentage
                ? `${rowData.deal.realtor[1].coopRates[2].percentage}%`
                : '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Third Commission Date',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              if (rowData.deal.realtor[1].coopRates.length > 2 && rowData.deal.realtor[1].coopRates[2].date) {
                return convertAllDates(rowData.deal.realtor[1].coopRates[2].date, 'PP');
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Realtor 2 Third Commission Amount',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length > 1) {
              if (rowData.deal.realtor[1].coopRates.length > 2 && rowData.deal.realtor[1].coopRates[2].fixedAmount) {
                return numToCurrencyDecimal.format(parseFloat(rowData.deal.realtor[1].coopRates[2].fixedAmount));
              } else if (rowData.deal.realtor[1].coopRates.length > 2 && rowData.deal.realtor[1].coopRates[2].percentage) {
                if (project.commissionIncludeOptions) {
                  return numToCurrencyDecimal.format(
                    netHST(
                      rowData.basePrice +
                        rowData.deal.options.reduce((a, b) => {
                          return a + b?.amount!;
                        }, 0),
                      project,
                    ) *
                      (parseFloat(rowData.deal.realtor[1].coopRates[2].percentage) / 100)
                  );
                } else {
                  return numToCurrencyDecimal.format(
                    netHST(rowData.basePrice, project) * (parseFloat(rowData.deal.realtor[1].coopRates[2].percentage) / 100)
                  );
                }
              } else return '-';
            } else return '-';
          } else {
            return '-';
          }
        },
      },
      {
        Header: 'Total Commission %',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              let totalPercentage = 0;
              rowData.deal.realtor.forEach((realtor: IDealRealtor) => {
                let coop = realtor.coopRates.reduce((a: any, b: any) => a + b.percentage, 0);
                totalPercentage += coop;
              });
              if (totalPercentage) {
                return `${totalPercentage}%`;
              } else return '-';
            } else return '-';
          } else return '-';
        },
      },
      {
        Header: 'Total Commission Amount',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.realtor.length) {
              let totalAmount = 0;
              rowData.deal.realtor.forEach((realtor: IDealRealtor) => {
                let amount = realtor.coopRates.reduce((a: any, b: any) => {
                  if (b.fixedAmount) {
                    return a + b.fixedAmount;
                  } else {
                    if (project.commissionIncludeOptions) {
                      return (
                        a +
                        netHST(
                          rowData.basePrice +
                            rowData.deal.options.reduce((a, b) => {
                              return a + b?.amount!;
                            }, 0),
                          project,
                        ) *
                          (parseFloat(b.percentage) / 100)
                      );
                    } else {
                      return a + netHST(rowData.basePrice, project) * (parseFloat(b.percentage) / 100);
                    }
                  }
                }, 0);
                totalAmount += amount;
              });
              if (totalAmount) {
                return numToCurrencyDecimal.format(totalAmount);
              } else return '-';
            } else return '-';
          } else return '-';
        },
      },
      {
        Header: 'Mortgage Received',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.mortgage.revisions.includes('No MPA Required')) {
              return 'No MPA Required';
            } else {
              return rowData.deal.mortgage.amount && rowData.deal.mortgage.getUrl
                ? numToCurrency.format(rowData.deal.mortgage.amount)
                : '-';
            }
          } else return '-';
        },
      },
      {
        Header: 'Mortgage Required',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            return numToCurrency.format(
              rowData.deal?.totalPrice! - rowData.deal.deposit.map((deposit) => deposit.amount || 0).reduce((a, b) => a + b, 0)
            );
          } else return '-';
        },
      },
      {
        Header: 'Mortgage Lender',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            if (rowData.deal.mortgage.lender) {
              return rowData.deal.mortgage.lender;
            } else return '-';
          }
          return '-';
        },
      },
      {
        Header: 'Total Deposits',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            return numToCurrency.format(rowData.deal.deposit.map((deposit) => deposit.amount || 0).reduce((a, b) => a + b, 0));
          } else return '-';
        },
      },
      {
        Header: 'Total Deposits (%)',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            const total = rowData.deal.deposit.map((deposit) => deposit.amount || 0).reduce((a, b) => a + b, 0);
            return Math.round((total / rowData.deal?.totalPrice!) * 10000) / 100 + '%';
          } else return '-';
        },
      },
      {
        Header: 'Total Deposits Cleared',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            return numToCurrency.format(
              rowData.deal.deposit
                .filter((deposit) => deposit.chequeType === 'cleared')
                .map((deposit) => deposit.amount || 0)
                .reduce((a, b) => a + b, 0)
            );
          } else return '-';
        },
      },
      {
        Header: 'Total Deposits Cleared (%)',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            const total = rowData.deal.deposit
              .filter((deposit) => deposit.chequeType === 'cleared')
              .map((deposit) => deposit.amount || 0)
              .reduce((a, b) => a + b, 0);
            return Math.round((total / rowData.deal?.totalPrice!) * 10000) / 100 + '%';
          } else return '-';
        },
      },
      {
        Header: 'All Deposits Check',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            const allDeposits = rowData.deal.deposit.every(
              (deposit) =>
                deposit.chequeType === 'received' ||
                deposit.chequeType === 'distributed' ||
                deposit.chequeType === 'cleared' ||
                deposit.chequeType === 'wire' ||
                deposit.chequeType === 'floating' ||
                deposit.chequeType === 'pad' ||
                deposit.name === 'Occupancy Deposit' ||
                deposit.name === 'Occupancy'
            );
            if (allDeposits) {
              return 'All Deposits Received';
            } else {
              return 'Deposits Missing';
            }
          } else return '-';
        },
      },
      {
        Header: '5% Cleared',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            const total = rowData.deal.deposit
              .filter((deposit) => deposit.chequeType === 'cleared')
              .map((deposit) => deposit.amount || 0)
              .reduce((a, b) => a + b, 0);
            return Math.round((total / rowData.deal?.totalPrice!) * 10000) / 100 >= 5 ? 'TRUE' : 'FALSE';
          } else return '-';
        },
      },
      {
        Header: 'Date 5% Clears',
        accessor: (rowData: IUnit) => {
          if (rowData.deal) {
            let total = 0;
            let date: string | Date = '';
            for (const deposit of rowData.deal.deposit) {
              total += deposit?.amount!;
              if (Math.round((total / rowData.deal?.totalPrice!) * 10000) / 100 >= 5) {
                date = convertAllDates(deposit.dueDate!, 'PP');
                break;
              }
            }
            return date;
          } else return '-';
        },
      },
    ];
  }, []);

  const handleModalType = (type: string) => {
    storeDispatch(handleModal(true));
    setType(type);
  };

  const modalContent = () => {
    if (type === 'custom') {
      return (
        <Box>
          <Typography variant={'h5'}>Add Columns</Typography>
          <p>Select the columns you would like to export</p>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              options={allColumns.slice(1)}
              getOptionLabel={(option: any) => option.Header}
              disableClearable={false}
              freeSolo={false}
              onChange={(event: any, newValue: any | null) => {
                if (newValue) {
                  setSelectedColumns([...selectedColumns, newValue]);
                }
              }}
              renderInput={(params) => <TextField {...params} label="Columns" size="small" />}
            />
          </Box>
          <p>
            <strong>Selected Columns</strong>
          </p>
          <Flex sx={{ my: 2, flexWrap: 'wrap' }}>
            {selectedColumns.map((column: any, index: number) => {
              return (
                <Box
                  key={index}
                  sx={{
                    padding: '10px',
                    border: '1px solid #2e453d',
                    borderRadius: '8px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    marginRight: '10px',
                    mt: 1,
                  }}
                >
                  <span>
                    {index + 1}. {column.Header}
                  </span>
                  {column.Header !== 'Suite' ? (
                    <CloseIcon
                      sx={{ cursor: 'pointer', ml: 1 }}
                      color="secondary"
                      onClick={() => setSelectedColumns(selectedColumns.filter((columns: any, numIndex: number) => numIndex !== index))}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Flex>
          <FlexBetween>
            <Button onClick={() => storeDispatch(handleModal(false))} variant="contained" color="info">
              Cancel
            </Button>
            <Box>
              <Button
                sx={{ mr: 1 }}
                onClick={() =>
                  exportClosing(`${project.name} ${convertAllDates(new Date(), 'P')} Custom Closing.csv`, selectedColumns, units)
                }
                variant="contained"
                color="success"
              >
                Download Excel
              </Button>
            </Box>
          </FlexBetween>
        </Box>
      );
    } else if (type === 'price') {
      let data = units.filter((unit: IUnit) => unit.deal && unit.basePrice !== unit.deal.basePrice);
      const columns = [
        {
          Header: 'Suite',
          accessor: (rowData: any) => rowData.suite,
        },
        {
          Header: 'Unit Price',
          accessor: (rowData: any) => numToCurrency.format(rowData.basePrice),
        },
        {
          Header: 'Deal Price',
          accessor: (rowData: any) => numToCurrency.format(rowData.deal.basePrice),
        },
      ];
      return <StandardTable data={data} columns={columns} />;
    } else if (type === 'options') {
      let data = units
        .map((unit: IUnit) => {
          let options: any[] = [];
          if (unit.deal) {
            options = unit.deal.options.map((option: any) => {
              return { ...option, suite: unit.suite, status: unit.status };
            });
          }
          return options;
        })
        .flat();

      let headers = [
        ...new Set(
          data.map((item) => {
            return item.amount / item.purchaseAmount;
          })
        ),
      ].sort(function (a, b) {
        return a - b;
      });
      let optionHeaders = [...new Set(data.map((item) => item.name))];
      let newData = optionHeaders.map((option: string) => {
        let keys = headers.reduce((acc: any, curr: any) => ((acc[curr] = 0), acc), {});
        for (const item of data) {
          if (item.name === option) {
            keys[item.amount] += item.purchaseAmount;
          }
        }
        return {
          name: option,
          ...keys,
        };
      });

      let columnHeaders = headers.map((header: number) => {
        return {
          Header: `${numToCurrency.format(header)}`,
          accessor: (rowData: any) => {
            let suites = data.filter((option: any) => option.name === rowData.name && option.amount / option.purchaseAmount === header);
            let value: any = `${rowData[header]}`;
            return (
              <Tooltip
                disableHoverListener={!rowData[header]}
                title={suites.length ? `${suites.map((option: any) => `${option.suite} (${option.status})`).join(', ')}` : ''}
              >
                <div>{value}</div>
              </Tooltip>
            );
          },
        };
      });

      const columns = [
        {
          Header: 'Name',
          accessor: (rowData: any) => {
            return rowData.name;
          },
        },
        ...columnHeaders,
      ];
      return <StandardTable data={newData} columns={columns} />;
    }
  };

  // network-only so every export uses the current drafts rather than a copy cached by an earlier export
  const [fetchPriceLists] = useLazyQuery(PRICELISTMANY, {
    variables: { filter: { project: project._id } },
    fetchPolicy: 'network-only',
  });

  // The IPL with its Summary table at the bottom of each sheet, or the exact same IPL without it
  const exportIpl = async (includeSummary: boolean = true) => {
    const { data } = await fetchPriceLists();
    if (!data) return;

    exportIplGrid(
      `${project.name} ${convertAllDates(new Date(), 'P')} IPL${includeSummary ? '' : ' (No Summary)'}.xlsx`,
      units,
      project.name,
      data.priceListMany as IPriceList[],
      project,
      includeSummary
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            '-webkit-transform': 'translate(-50%, -50%)',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <LoadingLogo />
        </Box>
      ) : (
        <div>
          <GlobalModal>{modalContent()}</GlobalModal>
          <div>
            <PageTitle title={'Closing Report'} />
          </div>
          {/* gap spaces the buttons apart when they wrap onto extra rows on small screens */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: { xs: 2, sm: 0 }, mb: { xs: 2, sm: 1 } }}>
            <Button
              onClick={() => {
                setPendingDeposits('original');
              }}
              variant="contained"
              color="primary"
            >
              Export to CSV
            </Button>
            <Button
              onClick={() => {
                setPendingDeposits('pending');
              }}
              variant="contained"
              color="primary"
            >
              Export to CSV (Pending Deposits)
            </Button>
            <Button onClick={() => handleModalType('custom')} variant="contained" color="primary">
              Create Custom Closing
            </Button>
            {user.type === 'Manager' || user.type === 'Admin' ? (
              <Button onClick={() => handleModalType('price')} variant="contained" color="primary">
                Price Sync
              </Button>
            ) : null}
            {user.type === 'Manager' || user.type === 'Admin' ? (
              <Button onClick={() => handleModalType('options')} variant="contained" color="primary">
                Options Sync
              </Button>
            ) : null}
            {user.type === 'Manager' || user.type === 'Admin' || user.type === 'Developer' ? (
              <>
                <Button onClick={() => exportIpl()} variant="contained" color="primary">
                  Export IPL
                </Button>
                <Button onClick={() => exportIpl(false)} variant="contained" color="primary">
                  Export IPL (No Summary)
                </Button>
              </>
            ) : null}
          </Box>
          <VirtualizedTable itemHeight={100} columns={allColumns} data={units} filter={true} tableHeight={1000} />
        </div>
      )}
    </Box>
  );
};

const PRICELISTMANY = gql`
  query priceListMany($filter: FilterFindManyPriceListInput) {
    priceListMany(filter: $filter, limit: 10000) {
      _id
      name
      draft {
        _id
        status
        suite
        unit
        unitId
        level
        modelType
        unitType
        basePrice
        size
        outdoor {
          name
          size
        }
        bathroom
        exposure
        ceilingHeight
      }
    }
  }
`;

const UNITCLOSING = gql`
  query unitClosing($project: MongoID!) {
    unitClosing(project: $project) {
      _id
      forfeitedDepositRevenue
      mrtAdditionalDeposit
      suite
      unit
      modelType
      unitType
      bathroom
      size
      basePrice
      level
      exposure
      outdoor {
        name
        size
      }
      status
      custom
      ceilingHeight
      type
      assignment {
        assignees {
          _id
          email
          dob
          streetAddress
          city
          province
          country
          postalCode
          employer
          occupation
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          proofExpiry
          proofNumber
          proofType
          unit
          idJurisdiction
          sin
          firstName
          lastName
          fullName
        }
        assignors {
          fullName
        }
        signDate
      }
      allocation {
        fullName
      }
      allocatedDate
      tempAllocation
      tier
      rental
      history {
        type
        description
        timestamp
      }
      maintenanceFee
      firstTentativeOccupancy
      finalTentativeOccupancy
      firmOccupancy
      outsideOccupancy
      deal {
        _id
        signDate
        firmDate
        executeDate
        type
        auditor {
          fullName
        }
        auditDate
        unit {
          suite
        }
        project {
          _id
        }
        purchasers {
          _id
          email
          dob
          streetAddress
          city
          province
          country
          postalCode
          employer
          occupation
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          proofExpiry
          proofNumber
          proofType
          unit
          idJurisdiction
          sin
          firstName
          lastName
          fullName
        }
        realtor {
          _id
          email
          firstName
          lastName
          fullName
          brokerage
          streetAddress
          city
          province
          country
          postalCode
          brokeragePhone
          brokerageFax
          directPhone
          coopRates {
            type
            days
            date
            description
            percentage
            fixedAmount
          }
        }
        deposit {
          name
          amount
          dueDate
          accountNumber
          chequeNumber
          chequeDate
          chequeType
          chequeAmount
        }
        pendingDeposits {
          name
          amount
          dueDate
          accountNumber
          chequeNumber
          chequeDate
          chequeType
          chequeAmount
        }
        additionalDeposits {
          name
          amount
          dueDate
          accountNumber
          chequeNumber
          chequeDate
          chequeType
          chequeAmount
        }
        options {
          name
          purchaseAmount
          amount
        }
        basePrice
        totalPrice
        tags
        adjustments {
          name
          type
          adjustmentType
          value
          approval
        }
        upgradeSetOne {
          date
          salesRep {
            fullName
          }
          collectionTemplate {
            name
          }
          upgradeSet {
            price
            quantity
            mainCategory
          }
        }
        upgradeSetTwo {
          date
          salesRep {
            fullName
          }
          collectionTemplate {
            name
          }
          upgradeSet {
            price
            quantity
            mainCategory
          }
        }
        upgradeSetThree {
          date
          salesRep {
            fullName
          }
          collectionTemplate {
            name
          }
          upgradeSet {
            price
            quantity
            mainCategory
          }
        }
        mortgage {
          lender
          revisions
          amount
          getUrl
          putUrl
        }
        documents {
          status
          type
          createdAt
          name
        }
        solicitor {
          solicitor
          firm
          streetAddress
          city
          province
          postalCode
          email
          primaryPhone
        }
        salesRep {
          fullName
          firstName
        }
        thirdParty {
          fullName
        }
        joinWaitlist
        createdAt
      }
    }
  }
`;

export default ClosingTable;
